/**
 * Card Render Service
 * Handles rendering of recommendation cards and empty states
 */

import { ActionItem, MapData } from '../types';
import { CardComponent } from '../components';
import { LimitService } from './limit.service';
import { CATEGORIES } from '../constants';

/**
 * Configuration for card rendering
 */
export interface CardRenderConfig {
  containerId: string;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: CardRenderConfig = {
  containerId: 'recommendations-container'
};

/**
 * Service for rendering recommendation cards
 */
export class CardRenderService {
  private cardComponent: CardComponent;
  private limitService: LimitService;
  private config: CardRenderConfig;

  constructor(cardComponent: CardComponent, config?: Partial<CardRenderConfig>) {
    this.cardComponent = cardComponent;
    this.limitService = new LimitService();
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Get the container element
   * @returns Container element or null
   */
  private getContainer(): HTMLElement | null {
    return document.getElementById(this.config.containerId);
  }

  /**
   * Render all recommendation cards
   * @param actions - Action items to render
   * @param topICE - Set of top ICE recommendation strings
   * @param mapData - Curated content map data
   * @returns Object with rendered count info
   */
  renderCards(
    actions: ActionItem[],
    topICE: Set<string>,
    mapData: MapData | null
  ): { total: number; rendered: number } {
    const container = this.getContainer();
    if (!container) {
      return { total: 0, rendered: 0 };
    }

    // Clear container
    container.innerHTML = '';

    // Apply limiting per secondary category
    const limitedActions = this.limitService.getLimitedActions(actions);

    // Render all limited cards
    limitedActions.forEach((action) => {
      const isTopICE = topICE.has(action.recommendation);
      const card = this.cardComponent.create(action, isTopICE, mapData);
      container.appendChild(card);
    });

    return {
      total: actions.length,
      rendered: limitedActions.length
    };
  }

  /**
   * Add empty state cards for categories with no recommendations
   * @param actions - All action items (to check which categories have recommendations)
   */
  addEmptyStateCards(actions: ActionItem[]): void {
    const container = this.getContainer();
    if (!container) return;

    CATEGORIES.forEach((category) => {
      const hasRecommendations = actions.some((action) => action.area === category);

      if (!hasRecommendations) {
        const emptyCard = this.createEmptyStateCard(category);
        container.appendChild(emptyCard);
      }
    });
  }

  /**
   * Create an empty state card for a category
   * @param category - Category name
   * @returns Empty state card element
   */
  private createEmptyStateCard(category: string): HTMLElement {
    const emptyCard = document.createElement('div');
    emptyCard.className = 'empty-state bg-gray-800/30 border border-gray-700/50 rounded-lg p-6 text-center';
    emptyCard.dataset.area = category;
    emptyCard.innerHTML = `
      <div class="text-gray-400">
        <svg class="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="text-sm font-medium">No recommendations at the moment, ${category} category is in good shape!</p>
      </div>
    `;
    return emptyCard;
  }

  /**
   * Clear all cards from the container
   */
  clearCards(): void {
    const container = this.getContainer();
    if (container) {
      container.innerHTML = '';
    }
  }

  /**
   * Get the card component instance
   * @returns CardComponent
   */
  getCardComponent(): CardComponent {
    return this.cardComponent;
  }
}
